import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import * as selectors from "selectors";
import actions from "actions";
import SectionWrapper from "../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import { ANLoadingScreen } from "@answersnow/pkg-theme";
import ViewFields from "./ViewFields";

const AUTH_TABS = {
  ongoing: "ongoing",
  secondary: "secondary",
} as const;

type AuthTab = (typeof AUTH_TABS)[keyof typeof AUTH_TABS];

/**
 * Authorizations component that manages its own state and data fetching.
 * This component handles insurance authorization codes independently with its own edit mode.
 * Extracts clientId from URL parameters - no props required.
 */
const Authorizations: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const [selectedTab, setSelectedTab] = useState<AuthTab>(AUTH_TABS.ongoing);

  const authorizationCodes: any[] = useSelector(selectors.getAuthorizationCodes) || [];
  const authorizationCodesLoading = useSelector(selectors.getCustomerAuthorizationCodesLoading);
  const availableInsuranceCodes: any[] = useSelector(selectors.getAvailableInsuranceCodes) || [];
  const clinicianList: any[] = useSelector(selectors.getCliniciansList) || [];

  const isCaregiver = useSelector(selectors.isCaregiver);
  const userPermissions: any = useSelector(selectors.getUserPermissionsList);
  const customerDetails: any = useSelector(selectors.getCustomerDetails);
  const silnaAssessmentAuthStatus = customerDetails?.details?.silna_assessment_auth_status || "";

  const canEdit = !isCaregiver && Boolean(userPermissions?.add_client_insurance_authorization);

  useEffect(() => {
    if (clientId) {
      dispatch(actions.getAuthorizationCodes(parseInt(clientId)));
      dispatch(actions.getAllAvailableInsuranceCodes());
    }
  }, [clientId, dispatch]);


  if (authorizationCodesLoading) {
    return <ANLoadingScreen />;
  }

  if (!clientId) {
    return (
      <Stack sx={{ p: 5 }}>
        <Typography variant="h6" color="error">
          Client ID is required
        </Typography>
      </Stack>
    );
  }

  return (
    <SectionWrapper
      title="Authorizations"
      description="List of approved ongoing and secondary insurance authorizations."
      hideButtons
    >
      {authorizationCodes.length > 0 && (
        <ToggleButtonGroup
          value={selectedTab}
          exclusive
          sx={{ width: "fit-content", mb: 4 }}
          onChange={(_, newValue) => {
            if (newValue !== null) setSelectedTab(newValue);
          }}
        >
          <ToggleButton value={AUTH_TABS.ongoing}>Ongoing Authorizations</ToggleButton>
          <ToggleButton value={AUTH_TABS.secondary}>Secondary Authorizations</ToggleButton>
        </ToggleButtonGroup>
      )}

      {authorizationCodes.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No Authorizations on the account
        </Typography>
      )}

      {canEdit ? (
        <EditFields
          clientId={clientId}
          authorizationCodes={authorizationCodes}
          availableInsuranceCodes={availableInsuranceCodes}
          clinicianList={clinicianList}
          userPermissions={userPermissions}
          silnaAssessmentAuthStatus={silnaAssessmentAuthStatus}
          selectedTab={selectedTab}
        />
      ) : (
        authorizationCodes.length > 0 && (
          <ViewFields
            authorizationCodes={authorizationCodes}
            userPermissions={userPermissions}
            selectedTab={selectedTab}
          />
        )
      )}
    </SectionWrapper>
  );
};

export default Authorizations;
